<template>
  <div class="center-panel">
    <div class="center-header">
      <span>安全运行天数</span>
      <div class="runday">
        <div v-for="(item, index) in safeUpDays" :key="index" class="number-item">{{ item }}</div>
      </div>
    </div>

    <div class="center-middle">
      <el-popover v-if="setting" trigger="click">
        <el-slider v-model="layout.mapAreaOpacity" />
        <template #reference>
          <el-icon size="25" class="edit-btn" title="四色图区域透明度"><Sunny /></el-icon>
        </template>
      </el-popover>

      <template v-if="setting">
        <el-scrollbar>
          <el-checkbox-group v-model="layout.mapIds">
            <div v-for="map in mapOptions" :key="map.id" class="checkbox-wrap">
              <el-image :src="getUrl(fileFolder.MAP_IMAGE, map.previewImage)" fit="cover" style="width: 100%; height: 100%" @click="$emit('toggle-map', map.id)" />
              <el-checkbox :value="map.id" :label="map.name" />
            </div>
          </el-checkbox-group>
        </el-scrollbar>
      </template>

      <template v-else>
        <el-carousel v-if="layout.mapIds.length > 0" trigger="click" height="100%" :interval="5000">
          <el-carousel-item v-for="(mapId, index) in sortedMapIds" :key="mapId" v-loading="mapRuntime[mapId]?.loading" element-loading-text="加载中...">
            <RiskMap
              v-model:loading="mapRuntime[mapId].loading"
              :ref="(el) => setRiskMapRef(index, el)"
              :map-id="mapId"
              :data="mapRuntime[mapId].data"
              :risk-levels="mapRuntime[mapId].riskLevels"
              :opacity="layout.mapAreaOpacity"
            />
          </el-carousel-item>
        </el-carousel>
        <el-text v-else type="warning">请设置要显示的风险四色图！</el-text>
      </template>

      <component :is="floatWidget" v-bind="floatProps" class="float-table" />
    </div>

    <div class="center-bottom">
      <chart-wrapper>
        <el-icon v-if="setting" class="edit-btn" size="18" @click="$emit('edit-meters')"><Edit /></el-icon>
        <div class="chart-content">
          <el-scrollbar height="100%">
            <div class="meter-wrap">
              <MeterChart
                v-for="(meter, index) in layout.meters"
                :key="index"
                class="meter"
                :label="meter.name || getKpiDefinition(meter.kpiId)?.name"
                :value="formatWidgetProps(meter.kpiId, kpiData[meterSlotKey(index)])"
              />
            </div>
          </el-scrollbar>
        </div>
      </chart-wrapper>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { getUrl, fileFolder } from '@/util/resource'
import { getKpiDefinition, getWidgetComponent } from '../config/kpiRegistry'
import { meterSlotKey, SLOT_KEYS } from '../config/slots'
import ChartWrapper from './ChartWrapper.vue'
import RiskMap from '../widget/RiskMap.vue'
import MeterChart from '../widget/MeterChart.vue'

const props = defineProps({
  layout: Object,
  safeUpDays: Array,
  setting: Boolean,
  mapOptions: Array,
  mapRuntime: Object,
  sortedMapIds: Array,
  kpiData: Object,
  formatWidgetProps: Function,
  setRiskMapRef: Function
})

defineEmits(['toggle-map', 'edit-meters'])

const floatWidget = computed(() => getWidgetComponent(props.layout.slots[SLOT_KEYS.CENTER_FLOAT].kpiId))
const floatProps = computed(() => props.formatWidgetProps(props.layout.slots[SLOT_KEYS.CENTER_FLOAT].kpiId, props.kpiData[SLOT_KEYS.CENTER_FLOAT]))
</script>

<style lang="scss" scoped>
.center-panel {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.center-header {
  width: 100%;
  height: 80px;
  background-position: center;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  font-size: 2em;
  color: rgb(15, 248, 255);
}
.runday {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  .number-item {
    width: 80px;
    height: 45px;
    background-image: url('@/assets/dashboard/num-bg.png');
    background-size: 80px 45px;
    background-repeat: no-repeat;
    background-color: rgb(28, 61, 103);
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.center-middle {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;

  .el-carousel {
    width: 100%;
    height: 100%;
  }
  .el-carousel__item {
    display: flex;
    justify-content: center;
    align-items: center;
    object-fit: cover;
  }
  :deep(.el-scrollbar__view) {
    height: 100%;
  }
  .el-checkbox-group {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 100%;
    width: fit-content;
    padding: 0 10px;
  }
  .checkbox-wrap {
    display: flex;
    flex-direction: column;
    gap: 5px;
    align-items: center;
    border: 1px solid var(--my-text-color);
    aspect-ratio: 4/3;
    height: 60%;
  }
  .float-table {
    position: absolute;
    left: 15px;
    bottom: 10px;
  }
  .edit-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    z-index: 99;
    color: var(--my-text-color);
  }
}
.center-bottom {
  width: 100%;
  height: 20%;

  .chart-item {
    width: 100%;
    height: 100%;
  }
  :deep(.el-scrollbar__view) {
    height: 100%;
  }
  .meter-wrap {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    background-color: unset;
    height: 100%;
  }
  .chart-content {
    width: 100%;
    height: 100%;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.2);
  }
  .edit-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    z-index: 99;
    color: var(--my-text-color);
  }
}
</style>
